import { forwardRef, useEffect, useMemo } from "react";
import {
    getPostSpirometryData,
    getPreSpirometryCalculatedValues,
    submitPreSprimetryData,
    validatePreSpirometryField,
} from "../../api/forms/spirometry";
import {
    PostSpirometryData,
    PreSpirometryData,
    SpirometryFormType,
    SpirometryValidationErrors,
    UpdateSpirometryValidationErrors,
} from "../../api/forms/spirometry/types";
import { Nullable } from "../../api/types";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import useTestId from "../../hooks/useTestId";
import { FormHandle, InputTypes } from "../../types";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

type Props = {
    formValues: PreSpirometryData | null | undefined;
    setFormValues: React.Dispatch<
        React.SetStateAction<PreSpirometryData | null | undefined>
    >;
    setPostFormValues: React.Dispatch<
        React.SetStateAction<PostSpirometryData | null | undefined>
    >;
    setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    validationErrors: SpirometryValidationErrors["pre"];
    updateValidationErrors: UpdateSpirometryValidationErrors;
};

export default forwardRef<
    FormHandle<Nullable<PreSpirometryData>> | null,
    Props
>(function PreSpirometyForm(props, ref) {
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.PRE_SPIROMETRY,
    });

    const testId = useTestId();

    const {
        formValues,
        setFormValues,
        setPostFormValues,
        setErrors,
        validationErrors,
        updateValidationErrors,
    } = props;

    useEffect(() => {
        if (formData) {
            setFormValues(formData as PreSpirometryData);
        }
    }, [formData, setFormValues]);

    const { submissionLoading } = useImperativeFormSubmit<PreSpirometryData>({
        formValues,
        formData,
        ref,
        onSubmitSuccess: setFromData,
        submitFunction: submitPreSprimetryData,
    });

    type FromFields =
        DataInputGenericFormProps<PreSpirometryData>["formFields"];

    const formFields = useMemo<FromFields>((): FromFields => {
        return !formData || !formValues
            ? []
            : [
                  {
                      id: "fvc",
                      label: "FVC (l)",
                      value: formValues.fvc.measured ?? undefined,
                      originalValue: formData.fvc.measured ?? undefined,
                      secondaryValue: formValues.fvc.percentOfPredicted,
                      withSecondaryValue: true,
                      type: InputTypes.NUMBER,
                      required: false,
                      error: validationErrors?.fvc,
                  },
                  {
                      id: "fev1",
                      label: "FEV1 (l)",
                      value: formValues.fev1.measured ?? undefined,
                      originalValue: formData.fev1.measured ?? undefined,
                      secondaryValue: formValues.fev1.percentOfPredicted,
                      withSecondaryValue: true,
                      type: InputTypes.NUMBER,
                      required: false,
                      error: validationErrors?.fev1,
                  },
                  {
                      id: "fev1_fvc",
                      label: "FEV1/FVC (%)",
                      value: formValues.fev1_fvc.measured ?? undefined,
                      originalValue: formData.fev1_fvc.measured ?? undefined,
                      secondaryValue: formValues.fev1_fvc.percentOfPredicted,
                      withSecondaryValue: true,
                      type: InputTypes.NUMBER,
                      required: false,
                      error: validationErrors?.fev1_fvc,
                      validation: {
                          isInteger: true,
                      },
                  },
                  {
                      id: "fef_25_75",
                      label: "FEF 25-75 (l)",
                      value: formValues.fef_25_75.measured ?? undefined,
                      originalValue: formData.fef_25_75.measured ?? undefined,
                      secondaryValue: formValues.fef_25_75.percentOfPredicted,
                      withSecondaryValue: true,
                      type: InputTypes.NUMBER,
                      required: false,
                      error: validationErrors?.fef_25_75,
                  },
                  {
                      id: "mvv",
                      label: "MVV (l/min)",
                      value: formValues.mvv.measured ?? undefined,
                      originalValue: formData.mvv.measured ?? undefined,
                      secondaryValue: formValues.mvv.percentOfPredicted,
                      withSecondaryValue: true,
                      type: InputTypes.NUMBER,
                      required: false,
                      error: validationErrors?.mvv,
                      validation: {
                          isInteger: true,
                      },
                  },
              ];
    }, [formData, formValues, validationErrors]);

    if (error) {
        return <Error />;
    }

    const onFromValuesChange = async ({
        value,
        fieldSettings: { id: fieldId },
    }: {
        value: Nullable<string | number>;
        fieldSettings: FromFields[number];
    }) => {
        const valueToSave =
            value === null || isNaN(Number(value))
                ? null // if not a number, don't update the value
                : Number(value);

        const validationRes = await validatePreSpirometryField({
            testId: testId!,
            fieldId,
            value: valueToSave,
        });

        const hasError = Boolean(!validationRes || validationRes?.[fieldId]?.error_message);

        updateValidationErrors("pre", {
            [fieldId]: hasError ? validationRes?.[fieldId].error_message : undefined,
        });

        setErrors((prev) => ({
            ...prev,
            [`preSpirometry_${fieldId}`]: hasError,
        }));

        const newValues = await getPreSpirometryCalculatedValues({
            value: valueToSave,
            fieldId,
            testId,
        });

        const newPostValues = (await getPostSpirometryData({
            testId: testId ?? "",
        })) as Nullable<PostSpirometryData>;

        setFormValues((prevFormValues: Nullable<PreSpirometryData>) => {
            if (!prevFormValues || !formData) {
                return prevFormValues;
            }
            return {
                ...prevFormValues,
                ...newValues,
            };
        });
        const updateValidationErrorsByFormType = (
            formType: SpirometryFormType,
            newSpirometryValues:
                | Partial<PreSpirometryData>
                | Partial<PostSpirometryData>
        ) => {
            const updatedValidationErrors: typeof validationErrors = {};
            for (const key in newSpirometryValues) {
                const _key = key as keyof typeof newSpirometryValues;
                updatedValidationErrors[_key] =
                    newSpirometryValues[_key]?.error_message;
            }
            updateValidationErrors(formType, updatedValidationErrors);
        };
        updateValidationErrorsByFormType("pre", newValues!);
        updateValidationErrorsByFormType("post", newPostValues!);

        setPostFormValues((prevFormValues: Nullable<PostSpirometryData>) => {
            if (!prevFormValues) {
                return prevFormValues;
            }
            return {
                ...prevFormValues,
                ...newPostValues,
            };
        });
    };

    return (
        <DataInputGenericForm
            formTitle="Pre CPET Spirometry"
            formFields={formFields}
            formData={formValues}
            onFromValuesChange={onFromValuesChange}
            loading={loading || submissionLoading}
            headers={["Parameter", "Meas.", "% of Pred."]}
        />
    );
});
